// Product Title Quality — Feed Health section, deterministic check (Merchant API).
// Brand-in-title coverage + title-length health, with examples of titles missing the brand.

import { count, pct } from '../../utils/format';
import type { EvidenceProps } from './registry';

interface TitleQualityShape {
  merchantId: string;
  productsReviewed: number;
  truncated: boolean;
  brandInTitlePct: number;
  missingBrandCount: number;
  avgTitleLength: number;
  shortCount: number;
  shortThreshold: number;
  truncationRiskCount: number;
  titleLimit: number;
  sampleMissingBrand: { offerId: string; title: string; brand: string }[];
}

export const TitleQualityEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as TitleQualityShape;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Title quality
          <span className='evidence__section-tag evidence__section-tag--context'>
            {count(e.productsReviewed)}
            {e.truncated ? '+' : ''} titled products · MC {e.merchantId}
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{pct(e.brandInTitlePct)}</div>
            <div className='audience-coverage__lbl'>Brand in title</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{count(e.avgTitleLength)}</div>
            <div className='audience-coverage__lbl'>Avg length (/{e.titleLimit})</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{count(e.shortCount)}</div>
            <div className='audience-coverage__lbl'>Too short (&lt;{e.shortThreshold})</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{count(e.truncationRiskCount)}</div>
            <div className='audience-coverage__lbl'>Truncation risk</div>
          </div>
        </div>
      </section>

      {e.sampleMissingBrand.length > 0 && (
        <section className='evidence__section'>
          <h4 className='evidence__section-title'>
            Titles missing the brand
            <span className='evidence__section-tag evidence__section-tag--context'>
              {count(e.missingBrandCount)} total · {e.sampleMissingBrand.length} shown
            </span>
          </h4>
          <table className='evidence__table'>
            <thead>
              <tr>
                <th>Title</th>
                <th>Brand</th>
              </tr>
            </thead>
            <tbody>
              {e.sampleMissingBrand.map(s => (
                <tr key={s.offerId}>
                  <td>{s.title}</td>
                  <td>{s.brand || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <p className='evidence__footnote'>
        Google matches search queries against the product title, so a title that omits the brand or
        leaves the {e.titleLimit}-char budget unused gives up matching surface; titles at/over the
        limit get truncated in the ad. Brand-in-title is matched on a word boundary to avoid false
        positives.
      </p>
    </div>
  );
};
